import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import Vas from 'constants/vas-codes';
import { switchExpiryDate } from 'utilities/card-utilities';
import * as yup from 'yup';
import _ from 'lodash';

// 'NC' VAS RSA and SNR codes in account defaults should disable their checkboxes
export const DISABLED_RSA_CODE = 'NC';
export const DISABLED_SNR_CODE = 'NC';

const EV_FLEET_LIMITER_CODE = 'EV';

export const isEvProductSelected = (values: Record<string, any>): boolean => values?.[EV_FLEET_LIMITER_CODE] === true;

export const cardHasRfid = (cardDetails: any): boolean => cardDetails?.hasRfid === true;

// Returns true only when EV is being *newly* enabled — wasn't selected before but is now.
// Prevents triggering RFID reissue when the user edits unrelated fields on a card that
// already had EV.
export const isEvNewlyAdded = (values: Record<string, any>, cardDetails: any): boolean => {
  if (!isEvProductSelected(values)) return false;
  const evWasPreviouslySelected = cardDetails?.fleetLimiters?.some(
    (fl: any) => fl.code === EV_FLEET_LIMITER_CODE && fl.selected === true
  );
  return !evWasPreviouslySelected;
};

export const getAddEditCardRequest = (
  values: Record<string, any>,
  fleetLimiters: any[],
  assignedGrandfatherClientNumber: string
) => {
  return {
    // Card Details
    embossedLastName: values?.embossedLastName?.toUpperCase()?.trim(),
    embossedCompanyName: values?.embossedCompanyName?.toUpperCase()?.trim(),
    issueDate: values?.issueDate,
    expiryDate: switchExpiryDate(values?.expiryDate),
    renewalOption: values?.renewalOption ? values.renewalOption : 'No Restrictions',
    costCentre: values?.costCentre?.toUpperCase()?.trim(),
    fuelPay: values?.fuelPay ? 'Enrolled' : 'Not Enrolled',
    driverName: values?.driverName?.toUpperCase()?.trim(),
    domesticNumber: values?.domesticNumber?.toUpperCase()?.trim(),
    ...getRequiredVasFieldsRequest(values),

    // Delivery Address
    contactFirstName: values?.contactFirstName?.trim(),
    contactLastName: values?.contactLastName?.trim(),
    addressLine1: values?.addressLine1?.trim(),
    addressLine2: values?.addressLine2?.trim(),
    city: values?.city?.trim(),
    state: values?.state,
    postcode: values?.postcode,

    // Card Usage Options
    ...getCardUsageOptionsRequest(values, fleetLimiters),

    // Other
    contractSearchMethod: 'CONTRACT_NUMBER',
    contractNumber: values?.contractNumber,
    contractIdentifier: values?.cardNumber,
    plasticType: values?.plasticType ?? values?.cardInfo?.plasticCardName,
    dispatchMode: 'MAIL',
    grandParentAccountNumber: assignedGrandfatherClientNumber,
  };
};

export const getRequiredVasFieldsRequest = (values: Record<string, any>) => {
  return {
    registrationNumber: values?.registrationNumber?.toUpperCase()?.trim(),
    registrationState: values?.registrationState,
    vehicleDescription: values?.vehicleDescription?.toUpperCase()?.trim(),
  };
};

export const getCardUsageOptionsRequest = (values: Record<string, any>, fleetLimiters: any[]) => {
  return {
    verifyPosPrompt: values?.verifyPosPrompt ? values.verifyPosPrompt : 'None',
    odometerRequired: values?.odometerRequired ? 'Yes' : 'No',
    orderNumberRequired: values?.orderNumberRequired ? 'Yes' : 'No',
    fuelPay: values?.fuelPay ? 'Enrolled' : 'Not Enrolled',
    monthlyLimit: values?.monthlyLimit,
    dailyLimit: values?.dailyLimit,
    roadsideAssist: values?.roadsideAssist ? values.vehicleType : 'Not Enrolled',
    serviceAndRepairs: values?.serviceAndRepairs ? 'Enrolled' : 'Not Enrolled',
    services: getFleetLimitersDesc(values, fleetLimiters),
  };
};

// Convert fleet limiter codes in object to array of fleet limiter descriptions
export const getFleetLimitersDesc = (values: Record<string, any>, fleetLimiters: any[]) => {
  return fleetLimiters
    .filter((item) => Object.keys(values).includes(item.code) && !!values[item.code])
    .map((item) => item.desc);
};

// Convert fleet limiter options array into fleet limiter codes object
export const getFleetLimiterCodes = (fleetLimitersArray: string[], fleetLimiters: any[]) => {
  const selectedFleetLimiters = fleetLimiters
    .filter((item) => fleetLimitersArray.includes(item.desc) && item.available)
    .map((item) => item.code);
  const unselectedFleetLimiters = fleetLimiters
    .filter((item) => !fleetLimitersArray.includes(item.desc) || !item.available)
    .map((item) => item.code);

  const fleetLimiterValues: ReadonlyArray<readonly [string, boolean]> = [
    ...selectedFleetLimiters.map((code: string) => [code, true] as const),
    ...unselectedFleetLimiters.map((code: string) => [code, false] as const),
  ];

  return fleetLimiterValues.reduce<Record<string, boolean>>(
    (codes, [code, selected]) => ({
      ...codes,
      [code]: selected,
    }),
    {}
  );
};

/**
 * Convert fleet limiter object into object with keys=fleetLimiterCodes, values=selected (truthy depending on if fleet limiter is selected and available)
 */
export const getFleetLimiterCodesObj = (fleetLimiters: any[] | undefined) => {
  return fleetLimiters?.reduce(
    (acc: Record<string, any>, option: any) => ({ ...acc, [option.code]: option.selected && option.available }),
    {}
  );
};

export const getEditCardDetails = (cardDetails: any) => {
  const rsaEnabled = cardDetails?.accountDefault?.vasRsaAccountCode !== Vas.RSA_NC;
  const snrEnabled = cardDetails?.accountDefault?.vasSnrAccountCode !== Vas.SNR_NC;
  const fuelPayEnabled = cardDetails?.accountDefault?.vasFuelPayAccountCode !== Vas.FP_NC;

  const rsaCardDetailsEnrolled = cardDetails?.roadsideAssist !== 'Not Enrolled';

  return {
    // Card Details
    embossedLastName: cardDetails?.embossedLastName,
    embossedCompanyName: cardDetails?.embossedCompanyName,
    registrationNumber: cardDetails?.registrationNumber,
    issueDate: cardDetails?.issueDate,
    expiryDate: switchExpiryDate(cardDetails?.expiryDate),
    renewalOption: cardDetails?.renewalOption ? cardDetails.renewalOption : 'No Restrictions',
    costCentre: cardDetails?.costCentre,
    fuelPay: fuelPayEnabled && cardDetails?.fuelPay === 'Enrolled',
    driverName: cardDetails?.driverName,
    domesticNumber: cardDetails?.domesticNumber,
    registrationState: cardDetails?.registrationState ?? '',
    vehicleDescription: cardDetails?.vehicleDescription ?? '',

    // Delivery Address
    contactFirstName: cardDetails?.contactFirstName ?? '',
    contactLastName: cardDetails?.contactLastName ?? '',
    addressLine1: cardDetails?.addressLine1 ?? '',
    addressLine2: cardDetails?.addressLine2 ?? '',
    city: cardDetails?.city ?? '',
    state: cardDetails?.state ?? '',
    postcode: cardDetails?.postcode ?? '',

    // Card Usage Options
    verifyPosPrompt: cardDetails?.verifyPosPrompt,
    odometerRequired: cardDetails?.odometerRequired === 'Yes',
    orderNumberRequired: cardDetails?.orderNumberRequired === 'Yes',
    monthlyLimit: cardDetails?.monthlyLimit,
    dailyLimit: cardDetails?.dailyLimit,

    roadsideAssist: rsaEnabled && rsaCardDetailsEnrolled,
    vehicleType: rsaEnabled && rsaCardDetailsEnrolled ? cardDetails?.roadsideAssist : undefined,
    serviceAndRepairs: snrEnabled && cardDetails?.serviceAndRepairs === 'Enrolled',

    // Other
    contractSearchMethod: 'CONTRACT_NUMBER',
    contractNumber: cardDetails?.contractNumber,
    cardNumber: cardDetails?.cardNumber,
    plasticType: cardDetails?.plasticTypeCode,
    termsAndConditions: rsaCardDetailsEnrolled,
  };
};

export const vasClassifierMapping = (code: string | undefined): string | null => {
  switch (code) {
    case Vas.RSA_H:
    case Vas.RSA_HC:
      return 'Heavy Vehicles';
    case Vas.RSA_P:
    case Vas.RSA_PC:
      return 'Passenger';
    case Vas.RSA_T:
    case Vas.RSA_TC:
      return 'Trucks';
    case Vas.RSA_YA:
      return null;
    default:
      return null;
  }
};

export const getCardUsageOptionsSchema = (
  fleetLimiterCodes: string[] | undefined,
  favouriteNamesArray: string[] = []
) => {
  return yup.object().shape({
    // Card Usage Options Validation
    cardFavoriteName: yup
      .string()
      .required(Validation.CARD_FAV_NAME_REQUIRED)
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .test('is-unique', Validation.CARD_FAV_NAME_UNIQUE, function (val) {
        return !favouriteNamesArray.includes(val?.toUpperCase() ?? '');
      }),
    roadsideAssist: yup.boolean(),
    fuelPay: yup.boolean(),
    vehicleType: yup
      .string()
      .nullable()
      .optional()
      .when('roadsideAssist', {
        is: true,
        then: yup.string().nullable().required(Validation.VEHICLE_TYPE_REQUIRED),
      }),
    termsAndConditions: yup
      .boolean()
      .optional()
      .when('roadsideAssist', {
        is: true,
        then: yup
          .boolean()
          .required(Validation.TERMS_AND_CONDITIONS_REQUIRED)
          .isTrue(Validation.TERMS_AND_CONDITIONS_REQUIRED),
      }),
    // Products and Services Validation
    allProducts: yup.boolean().when(fleetLimiterCodes ? fleetLimiterCodes.filter((code) => code) : [], {
      is: (...theArgs: any[]) => !theArgs.some((val) => val == true),
      then: yup.boolean().required(Validation.FLEET_LIMITERS_REQUIRED).isTrue(Validation.FLEET_LIMITERS_REQUIRED),
    }),
  });
};

export const getFavouritesDetails = (
  favSelected: any,
  fleetLimiters: any[] | undefined,
  vasRsaAccountCode: string | undefined,
  vasSnrAccountCode: string | undefined
) => {
  return {
    verifyPosPrompt: favSelected?.posPrompts?.verificationMethod,
    odometerRequired: favSelected?.posPrompts?.odometerRequired,
    orderNumberRequired: favSelected?.posPrompts?.orderNumberRequired,
    monthlyLimit: favSelected?.purchaseControl?.monthlyDollarLimit
      ? favSelected?.purchaseControl?.monthlyDollarLimit.toString()
      : undefined,
    dailyLimit: favSelected?.purchaseControl?.dailyDollarLimit
      ? favSelected?.purchaseControl?.dailyDollarLimit.toString()
      : undefined,
    allProducts: fleetLimiters?.every(
      (item) => !item.available || (item.available && favSelected.products.includes(item.desc))
    ),
    roadsideAssist: favSelected?.additionalProductAndServices.roadsideAssist && vasRsaAccountCode === Vas.RSA_YA,
    ...(favSelected?.additionalProductAndServices?.roadsideAssist &&
      vasRsaAccountCode === Vas.RSA_YA && { vehicleType: favSelected?.additionalProductAndServices?.vehicleType }),
    serviceAndRepairs: favSelected?.additionalProductAndServices?.serviceAndRepairs && vasSnrAccountCode === Vas.SNR_YA,
    termsAndConditions: favSelected?.additionalProductAndServices?.roadsideAssist && vasRsaAccountCode === Vas.RSA_YA,
    fuelPay: favSelected?.additionalProductAndServices?.fuelPay,
    ...getFleetLimiterCodes(favSelected?.products, fleetLimiters ?? []),
  };
};

export const getAddEditFavouriteRequest = (
  values: Record<string, any>,
  accountNumber: any,
  fleetLimiters: any[],
  cardOptionId: string | undefined = undefined
) => {
  return {
    accountNumber,
    id: cardOptionId,
    profiles: [
      {
        favouriteName: values?.cardFavoriteName?.toUpperCase(),
        id: values?.accountFavorites ? values.accountFavorites : '',
        additionalProductAndServices: {
          roadsideAssist: values?.roadsideAssist,
          serviceAndRepairs: values?.serviceAndRepairs,
          vehicleType: values?.vehicleType,
          fuelPay: values?.fuelPay,
        },
        posPrompts: {
          odometerRequired: values?.odometerRequired,
          orderNumberRequired: values?.orderNumberRequired,
          verificationMethod: values?.verifyPosPrompt,
        },
        purchaseControl: {
          dailyDollarLimit: values?.dailyLimit,
          monthlyDollarLimit: values?.monthlyLimit,
        },
        products: [...getFleetLimitersDesc(values, fleetLimiters)],
      },
    ],
  };
};

// Required card details fields if roadsideAssist or serviceAndRepairs are checked
export const getRequiredVasSchema = () => {
  return {
    registrationNumber: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(12, Validation.MAX_CHAR_TWELVE)
      .optional()
      .when(['roadsideAssist', 'serviceAndRepairs'], {
        is: (roadsideAssist: boolean, serviceAndRepairs: boolean) => roadsideAssist || serviceAndRepairs,
        then: yup
          .string()
          .nullable()
          .transform((value) => (value ? value.trim() : value))
          .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
          .max(12, Validation.MAX_CHAR_TWELVE)
          .required(Validation.REGISTRATION_NUMBER_REQUIRED),
      }),
    registrationState: yup
      .string()
      .nullable()
      .optional()
      .when(['roadsideAssist', 'serviceAndRepairs'], {
        is: (roadsideAssist: boolean, serviceAndRepairs: boolean) => roadsideAssist || serviceAndRepairs,
        then: yup.string().nullable().required(Validation.REGISTRATION_STATE_REQUIRED),
      }),
    vehicleDescription: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .max(50, Validation.MAX_CHAR_FIFTY)
      .optional()
      .when(['roadsideAssist', 'serviceAndRepairs'], {
        is: (roadsideAssist: boolean, serviceAndRepairs: boolean) => roadsideAssist || serviceAndRepairs,
        then: yup
          .string()
          .nullable()
          .transform((value) => (value ? value.trim() : value))
          .max(50, Validation.MAX_CHAR_FIFTY)
          .required(Validation.VEHICLE_DESCRIPTION_REQUIRED),
      }),
  };
};

export const isReissueForm = (cardDetails: any, values: Record<string, any>): boolean => {
  // Edits to these fields causes the card to be reissued
  const reissueFields = [
    { label: 'Embossing Name', value: 'embossedLastName' },
    { label: 'Embossing Line 2', value: 'embossedCompanyName' },
    { label: 'Expiry Date', value: 'expiryDate' },
    { label: 'Vehicle Registration', value: 'registrationNumber' },
    { label: 'Odometer Required', value: 'odometerRequired' },
    { label: 'Order No Required', value: 'orderNumberRequired' },
  ];

  const currentCardDetails = getEditCardDetails(cardDetails);

  const modifiedReissueFields = reissueFields
    .filter(({ value }) => {
      const preprocess = (val: any) => (val === '' || val === null ? '' : val);

      const originalValue = preprocess(currentCardDetails[value as keyof typeof currentCardDetails]);
      const editedValue = preprocess(values[value]);

      return !_.isEqual(editedValue, originalValue);
    })
    .map(({ label }) => label);

  return (
    modifiedReissueFields?.length > 0 &&
    cardDetails.nextCardNumber === '' &&
    cardDetails.cardStatus !== 'Requested - Not Issued'
  );
};
export const isBulkReissueRequired = (originalCards, values) => {
  const reissueFields = [
    'embossedLastName',
    'embossedCompanyName',
    'expiryDate',
    'registrationNumber',
    'odometerRequired',
    'orderNumberRequired',
  ];

  return originalCards.some((card) => {
    const current = getEditCardDetails(card);

    return reissueFields.some((field) => {
      return !_.isEqual(current[field], values[field]);
    });
  });
};
